import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDoc,
  setDoc,
} from "firebase/firestore";

const productsByType = {
  CBU: [
    "CBU VOICE",
    "CBU TERM",
    "CBU HUP",
    "CBU TVM HUP",
    "CBU HUP RD",
    "CBU TVM HUP RD",
    "CBU 5GHI",
    "CBU MBB",
  ],
  RPP: [
    "RPP VOICE",
    "RPP TERM",
    "RPP HUP",
    "RPP TVM HUP",
    "RPP HUP RD",
    "RPP TVM HUP RD",
    "RPP 5GHI",
    "RPP MBB",
  ],
  SMB: [
    "SMB VOICE",
    "SMB TERM",
    "SMB HUP",
    "SMB TVM HUP",
    "SMB HUP RD",
    "SMB TVM HUP RD",
    "SMB 5GHI",
    "SMB MBB",
    "SMB CABLE",
  ],
  FIDO: [
    "FIDO VOICE",
    "FIDO TERM",
    "FIDO HUP",
    "FIDO TVM HUP",
    "FIDO HUP RD",
    "FIDO TVM HUP RD",
    "FIDO MBB",
    "FIDO EXPU_N",
    "FIDO EXPU_H",
    "FIDO EXPU_V",
  ],
  Other: [
    "DP",
    "ACC",
    "EXPU_N",
    "EXPU_H",
    "EXPU_V",
    "MC APPROVED",
    "MC UNDERREVIEW",
    "CABLE",
    "CHATR",
    "OUTRIGHT SALE",
    "COMWAVE",
  ],
};

const isDPEligible = (name) =>
  name.includes("HUP") ||
  name.includes("TERM") ||
  name === "EXPU_H" ||
  name === "EXPU_N";

const DEBUG_DOC_PATH = {
  col: "adminSettings",
  id: "debugOverrides",
};

const REP_DEBUG_COLLECTION = "repDebugOverrides";
const PINNED_REP_ORDER = ["AA8", "DN", "MR5", "YK"];
const OTHER_REP_LABEL = "Other";

const EMPTY_DEBUG_OVERRIDES = {
  dpSold: 0,
  dpOpp: 0,
  mcEligible: 0,
  mcTotal: 0,
  mcApproved: 0,
  aalEligible: 0,
  aalHits: 0,
};

const EMPTY_NEW_PRODUCT = {
  name: "",
  category: "Other",
  active: true,
  countsInSales: false,
  affectsDP: false,
  affectsMC: false,
  mcExcluded: false,
  affectsAAL: false,
};

const normalizeDebugValue = (value) => {
  if (value === "" || value === "-" || Number.isNaN(Number(value))) return 0;
  return Number(value);
};

const sortRepsForStore = (repList) => {
  return [...repList].sort((a, b) => {
    const aName = (a?.name || "").trim();
    const bName = (b?.name || "").trim();

    const aIsOther = aName.toLowerCase() === OTHER_REP_LABEL.toLowerCase();
    const bIsOther = bName.toLowerCase() === OTHER_REP_LABEL.toLowerCase();

    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    const aPinnedIndex = PINNED_REP_ORDER.indexOf(aName);
    const bPinnedIndex = PINNED_REP_ORDER.indexOf(bName);

    const aIsPinned = aPinnedIndex !== -1;
    const bIsPinned = bPinnedIndex !== -1;

    if (aIsPinned && bIsPinned) return aPinnedIndex - bPinnedIndex;
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    return aName.localeCompare(bName);
  });
};

export default function Admin() {
  const [activeTab, setActiveTab] = useState("reps");

  const [reports, setReports] = useState([]);
  const [reps, setReps] = useState([]);
  const [products, setProducts] = useState([]);

  const [newRep, setNewRep] = useState("");
  const [newProduct, setNewProduct] = useState(EMPTY_NEW_PRODUCT);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedRep, setSelectedRep] = useState("ALL");
  const [search, setSearch] = useState("");

  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState({
    rep: "",
    date: "",
    time: "",
    customers: [],
  });

  const [loading, setLoading] = useState(true);
  const [adminNotice, setAdminNotice] = useState("");

  const [debugOverrides, setDebugOverrides] = useState(EMPTY_DEBUG_OVERRIDES);
  const [repDebugOverrides, setRepDebugOverrides] = useState(EMPTY_DEBUG_OVERRIDES);
  const [selectedDebugRep, setSelectedDebugRep] = useState("STORE");
  const [debugLoading, setDebugLoading] = useState(true);
  const [debugSaving, setDebugSaving] = useState(false);

  const activeDebugValues =
    selectedDebugRep === "STORE" ? debugOverrides : repDebugOverrides;

  const showNotice = (message) => {
    setAdminNotice(message);
    window.clearTimeout(showNotice._t);
    showNotice._t = window.setTimeout(() => setAdminNotice(""), 2200);
  };

  const fetchReps = async () => {
    const snapshot = await getDocs(collection(db, "reps"));
    const data = sortRepsForStore(
      snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((rep) => rep.name)
    );
    setReps(data);
  };

  const fetchReports = async () => {
    const snapshot = await getDocs(collection(db, "eodReports"));
    const data = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
    setReports(data);
  };

  const fetchProducts = async () => {
    const snapshot = await getDocs(collection(db, "products"));
    const data = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    setProducts(data);
  };

  const fetchDebugOverrides = async () => {
    setDebugLoading(true);
    try {
      const ref = doc(db, DEBUG_DOC_PATH.col, DEBUG_DOC_PATH.id);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();
        setDebugOverrides({
          dpSold: Number(data.dpSold || 0),
          dpOpp: Number(data.dpOpp || 0),
          mcEligible: Number(data.mcEligible || 0),
          mcTotal: Number(data.mcTotal || 0),
          mcApproved: Number(data.mcApproved || 0),
          aalEligible: Number(data.aalEligible || 0),
          aalHits: Number(data.aalHits || 0),
        });
      } else {
        setDebugOverrides(EMPTY_DEBUG_OVERRIDES);
      }
    } catch (err) {
      console.error(err);
      showNotice("Failed to load debug overrides");
    } finally {
      setDebugLoading(false);
    }
  };

  const fetchRepDebugOverrides = async (repCode) => {
    if (!repCode || repCode === "STORE") {
      setRepDebugOverrides(EMPTY_DEBUG_OVERRIDES);
      return;
    }

    setDebugLoading(true);
    try {
      const ref = doc(db, REP_DEBUG_COLLECTION, repCode);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();
        setRepDebugOverrides({
          dpSold: Number(data.dpSold || 0),
          dpOpp: Number(data.dpOpp || 0),
          mcEligible: Number(data.mcEligible || 0),
          mcTotal: Number(data.mcTotal || 0),
          mcApproved: Number(data.mcApproved || 0),
          aalEligible: Number(data.aalEligible || 0),
          aalHits: Number(data.aalHits || 0),
        });
      } else {
        setRepDebugOverrides(EMPTY_DEBUG_OVERRIDES);
      }
    } catch (err) {
      console.error(err);
      showNotice("Failed to load rep debug overrides");
    } finally {
      setDebugLoading(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchReps(),
        fetchReports(),
        fetchProducts(),
        fetchDebugOverrides(),
      ]);
    } catch (error) {
      console.error(error);
      showNotice("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (activeTab !== "debug") return;

    if (selectedDebugRep === "STORE") {
      fetchDebugOverrides();
    } else {
      fetchRepDebugOverrides(selectedDebugRep);
    }
  }, [activeTab, selectedDebugRep]);

  const filteredReports = useMemo(() => {
    return reports
      .filter((r) => {
        const dateMatch = selectedDate ? r.date === selectedDate : true;
        const repMatch = selectedRep === "ALL" ? true : r.rep === selectedRep;
        const searchTerm = search.trim().toLowerCase();
        const searchMatch = !searchTerm
          ? true
          : JSON.stringify(r).toLowerCase().includes(searchTerm);
        return dateMatch && repMatch && searchMatch;
      })
      .sort((a, b) => {
        const dateTimeA = new Date(`${a.date} ${a.time || "00:00"}`);
        const dateTimeB = new Date(`${b.date} ${b.time || "00:00"}`);
        return dateTimeB - dateTimeA;
      });
  }, [reports, selectedDate, selectedRep, search]);

  const handleAddRep = async () => {
    const name = newRep.trim();
    if (!name) return;

    const exists = reps.some(
      (rep) => rep.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      showNotice("Rep already exists");
      return;
    }

    try {
      await addDoc(collection(db, "reps"), { name });
      setNewRep("");
      await fetchReps();
      showNotice("Rep added");
    } catch (error) {
      console.error(error);
      showNotice("Failed to add rep");
    }
  };

  const handleDeleteRep = async (repId, repName) => {
    const confirmed = window.confirm(`Delete rep "${repName}"?`);
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "reps", repId));
      await fetchReps();
      showNotice("Rep deleted");
    } catch (error) {
      console.error(error);
      showNotice("Failed to delete rep");
    }
  };

  const handleNewProductChange = (field, value) => {
    setNewProduct((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddProduct = async () => {
    const trimmedName = newProduct.name.trim();
    if (!trimmedName) {
      showNotice("Product name is required");
      return;
    }

    const exists = products.some(
      (product) =>
        (product.name || "").trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (exists) {
      showNotice("Product already exists");
      return;
    }

    try {
      await addDoc(collection(db, "products"), {
        name: trimmedName,
        category: newProduct.category || "Other",
        active: !!newProduct.active,
        countsInSales: !!newProduct.countsInSales,
        affectsDP: !!newProduct.affectsDP,
        affectsMC: !!newProduct.affectsMC,
        mcExcluded: !!newProduct.mcExcluded,
        affectsAAL: !!newProduct.affectsAAL,
      });

      setNewProduct(EMPTY_NEW_PRODUCT);
      await fetchProducts();
      showNotice("Product added");
    } catch (error) {
      console.error(error);
      showNotice("Failed to add product");
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    const confirmed = window.confirm(
      `Delete product "${productName || "Unnamed product"}"?`
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "products", productId));
      await fetchProducts();
      showNotice("Product deleted");
    } catch (error) {
      console.error(error);
      showNotice("Failed to delete product");
    }
  };

  const handleUpdateProduct = async (productId, updates) => {
    try {
      if (typeof updates.name === "string") {
        const trimmedName = updates.name.trim();

        if (!trimmedName) {
          showNotice("Product name cannot be blank");
          return;
        }

        const exists = products.some(
          (product) =>
            product.id !== productId &&
            (product.name || "").trim().toLowerCase() ===
              trimmedName.toLowerCase()
        );

        if (exists) {
          showNotice("Another product already has that name");
          return;
        }

        updates = { ...updates, name: trimmedName };
      }

      await updateDoc(doc(db, "products", productId), updates);
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, ...updates } : p))
      );
      showNotice("Product updated");
    } catch (error) {
      console.error(error);
      showNotice("Failed to update product");
    }
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setEditForm({
      rep: report.rep || "",
      date: report.date || "",
      time: report.time || "",
      customers: JSON.parse(JSON.stringify(report.customers || [])),
    });
  };

  const closeEditModal = () => {
    setEditingReport(null);
    setEditForm({
      rep: "",
      date: "",
      time: "",
      customers: [],
    });
  };

  const updateCustomerField = (custIndex, field, value) => {
    setEditForm((prev) => {
      const next = { ...prev };
      next.customers = [...prev.customers];
      next.customers[custIndex] = {
        ...next.customers[custIndex],
        [field]: value,
      };
      return next;
    });
  };

  const changeCustomerAAL = (custIndex, delta) => {
    setEditForm((prev) => {
      const next = { ...prev };
      next.customers = [...prev.customers];
      const customer = { ...next.customers[custIndex] };
      customer.aalCount = Math.max(0, (customer.aalCount || 0) + delta);
      next.customers[custIndex] = customer;
      return next;
    });
  };

  const removeCustomer = (custIndex) => {
    setEditForm((prev) => ({
      ...prev,
      customers: prev.customers.filter((_, i) => i !== custIndex),
    }));
  };

  const addCustomer = () => {
    setEditForm((prev) => ({
      ...prev,
      customers: [
        ...prev.customers,
        {
          name: "",
          selectedTypes: [],
          products: [],
          hadMCBefore: false,
          aalCount: 0,
        },
      ],
    }));
  };

  const toggleCustomerType = (custIndex, type) => {
    setEditForm((prev) => {
      const next = { ...prev, customers: [...prev.customers] };
      const cust = {
        ...next.customers[custIndex],
        selectedTypes: [...(next.customers[custIndex].selectedTypes || [])],
        products: [...(next.customers[custIndex].products || [])],
      };

      if (cust.selectedTypes.includes(type)) {
        cust.selectedTypes = cust.selectedTypes.filter((t) => t !== type);
        cust.products = cust.products.filter(
          (p) => !productsByType[type].includes(p.name)
        );
      } else {
        cust.selectedTypes.push(type);
        productsByType[type].forEach((product) => {
          if (!cust.products.find((p) => p.name === product)) {
            cust.products.push({ name: product, qty: 0, dp: [] });
          }
        });
      }

      next.customers[custIndex] = cust;
      return next;
    });
  };

  const changeProductQty = (custIndex, productName, delta) => {
    setEditForm((prev) => {
      const next = { ...prev, customers: [...prev.customers] };
      const cust = {
        ...next.customers[custIndex],
        products: [...(next.customers[custIndex].products || [])],
      };

      cust.products = cust.products.map((p) => {
        if (p.name !== productName) return p;

        const newQty = Math.max(0, (p.qty || 0) + delta);
        let dpArray = [...(p.dp || [])];

        if (isDPEligible(p.name)) {
          if (delta > 0) dpArray.push({ value: "NONE" });
          if (delta < 0) dpArray = dpArray.slice(0, -1);
        }

        return { ...p, qty: newQty, dp: dpArray };
      });

      next.customers[custIndex] = cust;
      return next;
    });
  };

  const setDPValue = (custIndex, productName, dpIndex, value) => {
    setEditForm((prev) => {
      const next = { ...prev, customers: [...prev.customers] };
      const cust = {
        ...next.customers[custIndex],
        products: [...(next.customers[custIndex].products || [])],
      };

      cust.products = cust.products.map((p) => {
        if (p.name !== productName) return p;

        const nextDP = [...(p.dp || [])];
        if (!nextDP[dpIndex]) return p;
        nextDP[dpIndex] = { value };

        return { ...p, dp: nextDP };
      });

      next.customers[custIndex] = cust;
      return next;
    });
  };

  const handleSaveReport = async () => {
    if (!editingReport?.id) return;

    try {
      await updateDoc(doc(db, "eodReports", editingReport.id), {
        rep: editForm.rep,
        date: editForm.date,
        time: editForm.time,
        customers: editForm.customers,
      });
      await fetchReports();
      closeEditModal();
      showNotice("Report updated");
    } catch (error) {
      console.error(error);
      showNotice("Update failed");
    }
  };

  const handleDeleteReport = async (reportId) => {
    const confirmed = window.confirm("Delete this report?");
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "eodReports", reportId));
      await fetchReports();
      showNotice("Report deleted");
    } catch (error) {
      console.error(error);
      showNotice("Delete failed");
    }
  };

  const handleDebugChange = (field, value) => {
    const setter =
      selectedDebugRep === "STORE" ? setDebugOverrides : setRepDebugOverrides;

    if (value === "" || value === "-") {
      setter((prev) => ({
        ...prev,
        [field]: value,
      }));
      return;
    }

    if (/^-?\d+$/.test(value)) {
      setter((prev) => ({
        ...prev,
        [field]: Number(value),
      }));
    }
  };

  const handleSaveDebug = async () => {
    setDebugSaving(true);
    try {
      const source =
        selectedDebugRep === "STORE" ? debugOverrides : repDebugOverrides;

      const normalized = {
        dpSold: normalizeDebugValue(source.dpSold),
        dpOpp: normalizeDebugValue(source.dpOpp),
        mcEligible: normalizeDebugValue(source.mcEligible),
        mcTotal: normalizeDebugValue(source.mcTotal),
        mcApproved: normalizeDebugValue(source.mcApproved),
        aalEligible: normalizeDebugValue(source.aalEligible),
        aalHits: normalizeDebugValue(source.aalHits),
      };

      const ref =
        selectedDebugRep === "STORE"
          ? doc(db, DEBUG_DOC_PATH.col, DEBUG_DOC_PATH.id)
          : doc(db, REP_DEBUG_COLLECTION, selectedDebugRep);

      await setDoc(ref, normalized, { merge: true });

      if (selectedDebugRep === "STORE") {
        setDebugOverrides(normalized);
        showNotice("Store debug overrides saved");
      } else {
        setRepDebugOverrides(normalized);
        showNotice(`${selectedDebugRep} debug overrides saved`);
      }
    } catch (err) {
      console.error(err);
      showNotice("Failed to save debug overrides");
    } finally {
      setDebugSaving(false);
    }
  };

  const handleResetDebug = async () => {
    const label = selectedDebugRep === "STORE" ? "store" : selectedDebugRep;
    const confirmed = window.confirm(
      `Reset all ${label} debug overrides back to 0?`
    );
    if (!confirmed) return;

    if (selectedDebugRep === "STORE") {
      setDebugOverrides(EMPTY_DEBUG_OVERRIDES);
    } else {
      setRepDebugOverrides(EMPTY_DEBUG_OVERRIDES);
    }

    try {
      const ref =
        selectedDebugRep === "STORE"
          ? doc(db, DEBUG_DOC_PATH.col, DEBUG_DOC_PATH.id)
          : doc(db, REP_DEBUG_COLLECTION, selectedDebugRep);

      await setDoc(ref, EMPTY_DEBUG_OVERRIDES, { merge: true });
      showNotice(
        selectedDebugRep === "STORE"
          ? "Store debug overrides reset"
          : `${selectedDebugRep} debug overrides reset`
      );
    } catch (err) {
      console.error(err);
      showNotice("Failed to reset overrides");
    }
  };

  return (
    <div className="admin-panel-wrap">
      {adminNotice && <div className="save-popup success">{adminNotice}</div>}

      <div className="admin-header-card">
        <h2 className="admin-section-title">Admin Panel</h2>
        <p className="admin-section-subtext">
          Manage reps, products, reports, and debug overrides.
        </p>
      </div>

      <div
        className="admin-card"
        style={{ marginBottom: "16px", padding: "12px" }}
      >
        <div
          className="type-buttons"
          style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
        >
          <button
            type="button"
            className={`type-btn ${activeTab === "reps" ? "active" : ""}`}
            onClick={() => setActiveTab("reps")}
          >
            Reps
          </button>
          <button
            type="button"
            className={`type-btn ${activeTab === "products" ? "active" : ""}`}
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
          <button
            type="button"
            className={`type-btn ${activeTab === "reports" ? "active" : ""}`}
            onClick={() => setActiveTab("reports")}
          >
            Reports
          </button>
          <button
            type="button"
            className={`type-btn ${activeTab === "debug" ? "active" : ""}`}
            onClick={() => setActiveTab("debug")}
          >
            Debug Overrides
          </button>
        </div>
      </div>

      {activeTab === "reps" && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h3 className="admin-card-title">Rep Management</h3>
            <div className="admin-chip">{reps.length} reps</div>
          </div>

          <div className="admin-inline-form">
            <input
              className="admin-input"
              type="text"
              placeholder="Add rep name"
              value={newRep}
              onChange={(e) => setNewRep(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddRep();
              }}
            />
            <button
              className="display-btn admin-inline-btn"
              onClick={handleAddRep}
            >
              Add Rep
            </button>
          </div>

          <div className="admin-list">
            {loading ? (
              <div className="admin-empty">Loading reps...</div>
            ) : reps.length === 0 ? (
              <div className="admin-empty">No reps found.</div>
            ) : (
              reps.map((rep) => (
                <div className="admin-list-row" key={rep.id}>
                  <div className="admin-list-main">
                    <div className="admin-list-title">{rep.name}</div>
                  </div>
                  <button
                    className="secondary-btn admin-danger-btn"
                    onClick={() => handleDeleteRep(rep.id, rep.name)}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {activeTab === "products" && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h3 className="admin-card-title">Product Management</h3>
            <div className="admin-chip">{products.length} products</div>
          </div>

          <div
            className="admin-card"
            style={{ marginBottom: "16px", padding: "16px" }}
          >
            <div className="admin-card-head" style={{ marginBottom: "12px" }}>
              <h4 className="admin-card-title">Add New Product</h4>
            </div>

            <div
              className="admin-filters-grid"
              style={{ marginBottom: "12px" }}
            >
              <input
                className="admin-input"
                type="text"
                placeholder="Product name"
                value={newProduct.name}
                onChange={(e) =>
                  handleNewProductChange("name", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddProduct();
                }}
              />
              <select
                className="admin-input"
                value={newProduct.category}
                onChange={(e) =>
                  handleNewProductChange("category", e.target.value)
                }
              >
                <option value="CBU">CBU</option>
                <option value="RPP">RPP</option>
                <option value="SMB">SMB</option>
                <option value="FIDO">FIDO</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div
              className="admin-list-meta"
              style={{
                display: "flex",
                gap: "14px",
                flexWrap: "wrap",
                marginBottom: "14px",
              }}
            >
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.active}
                  onChange={(e) =>
                    handleNewProductChange("active", e.target.checked)
                  }
                />
                Active
              </label>

              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.countsInSales}
                  onChange={(e) =>
                    handleNewProductChange("countsInSales", e.target.checked)
                  }
                />
                Counts in Sales
              </label>

              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.affectsDP}
                  onChange={(e) =>
                    handleNewProductChange("affectsDP", e.target.checked)
                  }
                />
                Affects DP
              </label>

              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.affectsMC}
                  onChange={(e) =>
                    handleNewProductChange("affectsMC", e.target.checked)
                  }
                />
                Affects MC
              </label>

              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.mcExcluded}
                  onChange={(e) =>
                    handleNewProductChange("mcExcluded", e.target.checked)
                  }
                />
                Excluded from MC base
              </label>

              <label className="check-label">
                <input
                  type="checkbox"
                  checked={!!newProduct.affectsAAL}
                  onChange={(e) =>
                    handleNewProductChange("affectsAAL", e.target.checked)
                  }
                />
                Affects AAL
              </label>
            </div>

            <button className="display-btn" onClick={handleAddProduct}>
              Add Product
            </button>
          </div>

          <div className="admin-list">
            {loading ? (
              <div className="admin-empty">Loading products...</div>
            ) : products.length === 0 ? (
              <div className="admin-empty">No products found.</div>
            ) : (
              products.map((product) => (
                <div className="admin-list-row" key={product.id}>
                  <div className="admin-list-main">
                    <div className="admin-list-title">
                      <input
                        className="admin-input"
                        type="text"
                        defaultValue={product.name || ""}
                        onBlur={(e) =>
                          handleUpdateProduct(product.id, {
                            name: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.currentTarget.blur();
                          }
                        }}
                      />
                    </div>

                    <div className="admin-list-meta">
                      <span style={{ marginRight: 8 }}>Category:</span>
                      <select
                        className="admin-input"
                        style={{ maxWidth: 160, display: "inline-block" }}
                        value={product.category || ""}
                        onChange={(e) =>
                          handleUpdateProduct(product.id, {
                            category: e.target.value,
                          })
                        }
                      >
                        <option value="CBU">CBU</option>
                        <option value="RPP">RPP</option>
                        <option value="SMB">SMB</option>
                        <option value="FIDO">FIDO</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div
                      className="admin-list-meta"
                      style={{
                        display: "flex",
                        gap: "14px",
                        flexWrap: "wrap",
                        marginTop: "8px",
                      }}
                    >
                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.active}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              active: e.target.checked,
                            })
                          }
                        />
                        Active
                      </label>

                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.countsInSales}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              countsInSales: e.target.checked,
                            })
                          }
                        />
                        Counts in Sales
                      </label>

                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.affectsDP}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              affectsDP: e.target.checked,
                            })
                          }
                        />
                        Affects DP
                      </label>

                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.affectsMC}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              affectsMC: e.target.checked,
                            })
                          }
                        />
                        Affects MC
                      </label>

                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.mcExcluded}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              mcExcluded: e.target.checked,
                            })
                          }
                        />
                        Excluded from MC base
                      </label>

                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!product.affectsAAL}
                          onChange={(e) =>
                            handleUpdateProduct(product.id, {
                              affectsAAL: e.target.checked,
                            })
                          }
                        />
                        Affects AAL
                      </label>
                    </div>
                  </div>

                  <div className="admin-row-actions">
                    <button
                      className="secondary-btn admin-danger-btn"
                      onClick={() =>
                        handleDeleteProduct(product.id, product.name)
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {activeTab === "reports" && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h3 className="admin-card-title">Report Management</h3>
            <div className="admin-chip">{filteredReports.length} reports</div>
          </div>

          <div className="admin-filters-grid">
            <input
              className="admin-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />

            <select
              className="admin-input"
              value={selectedRep}
              onChange={(e) => setSelectedRep(e.target.value)}
            >
              <option value="ALL">All Reps</option>
              {reps.map((rep) => (
                <option key={rep.id} value={rep.name}>
                  {rep.name}
                </option>
              ))}
            </select>

            <input
              className="admin-input"
              type="text"
              placeholder="Search any report content"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="admin-list">
            {loading ? (
              <div className="admin-empty">Loading reports...</div>
            ) : filteredReports.length === 0 ? (
              <div className="admin-empty">No reports found.</div>
            ) : (
              filteredReports.map((report) => (
                <div
                  className="admin-list-row admin-report-row"
                  key={report.id}
                >
                  <div className="admin-list-main">
                    <div className="admin-list-title">
                      {report.rep || "No rep"} — {report.date || "No date"}{" "}
                      {report.time || ""}
                    </div>
                    <div className="admin-list-meta">
                      Customers: {report.customers?.length || 0}
                    </div>
                    <div className="admin-list-meta">
                      Firestore ID: {report.id}
                    </div>
                  </div>

                  <div className="admin-row-actions">
                    <button
                      className="secondary-btn"
                      onClick={() => openEditModal(report)}
                    >
                      Edit
                    </button>
                    <button
                      className="secondary-btn admin-danger-btn"
                      onClick={() => handleDeleteReport(report.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {activeTab === "debug" && (
        <section className="admin-card">
          <div className="admin-card-head">
            <h3 className="admin-card-title">Debug Overrides</h3>
            <div className="admin-chip">
              These values can be positive or negative and are applied on top of
              real calculated totals.
            </div>
          </div>

          {debugLoading ? (
            <div className="admin-empty">Loading overrides...</div>
          ) : (
            <>
              <div className="admin-filters-grid" style={{ marginBottom: "16px" }}>
                <div className="admin-inline-form">
                  <label className="admin-list-meta">Override Target</label>
                  <select
                    className="admin-input"
                    value={selectedDebugRep}
                    onChange={(e) => setSelectedDebugRep(e.target.value)}
                  >
                    <option value="STORE">Store</option>
                    {reps.map((rep) => (
                      <option key={rep.id} value={rep.name}>
                        {rep.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="admin-filters-grid">
                <div className="admin-inline-form">
                  <label className="admin-list-meta">DP Sold override</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.dpSold)}
                    onChange={(e) =>
                      handleDebugChange("dpSold", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">DP Opp override</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.dpOpp)}
                    onChange={(e) =>
                      handleDebugChange("dpOpp", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">MC Eligible override</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.mcEligible)}
                    onChange={(e) =>
                      handleDebugChange("mcEligible", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">MC Total override</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.mcTotal)}
                    onChange={(e) =>
                      handleDebugChange("mcTotal", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">
                    MC Approved override
                  </label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.mcApproved)}
                    onChange={(e) =>
                      handleDebugChange("mcApproved", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">
                    AAL Eligible override
                  </label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.aalEligible)}
                    onChange={(e) =>
                      handleDebugChange("aalEligible", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="admin-inline-form">
                  <label className="admin-list-meta">AAL Hits override</label>
                  <input
                    className="admin-input"
                    type="text"
                    value={String(activeDebugValues.aalHits)}
                    onChange={(e) =>
                      handleDebugChange("aalHits", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>
              </div>

              <p className="admin-section-subtext" style={{ marginTop: 12 }}>
                Use positive values to add and negative values to remove. For
                example, entering -1 for DP Sold will subtract 1 from the final
                Attach result shown to users.
              </p>

              <div className="admin-modal-actions" style={{ marginTop: 20 }}>
                <button
                  className="secondary-btn admin-danger-btn"
                  onClick={handleResetDebug}
                  disabled={debugSaving}
                >
                  Reset to 0
                </button>
                <button
                  className="display-btn"
                  onClick={handleSaveDebug}
                  disabled={debugSaving}
                >
                  {debugSaving ? "Saving..." : "Save Overrides"}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {editingReport && (
        <div className="admin-modal-backdrop" onClick={closeEditModal}>
          <div
            className="admin-modal admin-report-editor-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-title">Edit Report</div>

            <div className="admin-edit-top-grid">
              <select
                className="admin-modal-input"
                value={editForm.rep}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, rep: e.target.value }))
                }
              >
                <option value="">Select rep</option>
                {reps.map((rep) => (
                  <option key={rep.id} value={rep.name}>
                    {rep.name}
                  </option>
                ))}
              </select>

              <input
                className="admin-modal-input"
                type="date"
                value={editForm.date}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, date: e.target.value }))
                }
              />

              <input
                className="admin-modal-input"
                type="text"
                placeholder="Time"
                value={editForm.time}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, time: e.target.value }))
                }
              />
            </div>

            <div className="admin-editor-toolbar">
              <button className="secondary-btn" onClick={addCustomer}>
                + Add Customer
              </button>
            </div>

            <div className="admin-customer-editor-list">
              {editForm.customers.length === 0 ? (
                <div className="admin-empty">No customers in this report.</div>
              ) : (
                editForm.customers.map((cust, custIndex) => (
                  <div
                    className="admin-customer-editor-card"
                    key={custIndex}
                  >
                    <div className="admin-customer-editor-head">
                      <div className="admin-list-title">
                        Customer {custIndex + 1}
                      </div>
                      <button
                        className="secondary-btn admin-danger-btn"
                        onClick={() => removeCustomer(custIndex)}
                      >
                        Remove
                      </button>
                    </div>

                    <input
                      className="admin-modal-input"
                      type="text"
                      placeholder="Customer name"
                      value={cust.name || ""}
                      onChange={(e) =>
                        updateCustomerField(
                          custIndex,
                          "name",
                          e.target.value
                        )
                      }
                    />

                    <div className="admin-customer-flags">
                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={!!cust.hadMCBefore}
                          onChange={() =>
                            updateCustomerField(
                              custIndex,
                              "hadMCBefore",
                              !cust.hadMCBefore
                            )
                          }
                        />
                        Existing MC
                      </label>

                      <div className="aal-counter-wrap">
                        <span className="aal-label">AAL</span>
                        <div className="aal-counter">
                          <button
                            type="button"
                            className="qty-btn qty-btn-outline"
                            onClick={() => changeCustomerAAL(custIndex, -1)}
                          >
                            -
                          </button>

                          <span className="qty-value">
                            {cust.aalCount || 0}
                          </span>

                          <button
                            type="button"
                            className="qty-btn qty-btn-filled"
                            onClick={() => changeCustomerAAL(custIndex, 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="type-buttons admin-editor-type-buttons">
                      {Object.keys(productsByType).map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`type-btn ${
                            cust.selectedTypes?.includes(type)
                              ? "active"
                              : ""
                          }`}
                          onClick={() => toggleCustomerType(custIndex, type)}
                        >
                          {type}
                        </button>
                      ))}
                    </div>

                    {(cust.selectedTypes || []).map((type) => (
                      <div key={type} className="type-section">
                        <h4 className="type-title">{type}</h4>

                        {(cust.products || [])
                          .filter((p) => productsByType[type].includes(p.name))
                          .map((p) => (
                            <div
                              key={p.name}
                              className="product-row-card"
                            >
                              <div className="product-name">{p.name}</div>

                              <div className="qty-controls">
                                <button
                                  type="button"
                                  className="qty-btn qty-btn-outline"
                                  onClick={() =>
                                    changeProductQty(custIndex, p.name, -1)
                                  }
                                >
                                  -
                                </button>

                                <span className="qty-value">
                                  {p.qty || 0}
                                </span>

                                <button
                                  type="button"
                                  className="qty-btn qty-btn-filled"
                                  onClick={() =>
                                    changeProductQty(custIndex, p.name, 1)
                                  }
                                >
                                  +
                                </button>
                              </div>

                              {isDPEligible(p.name) &&
                                (p.dp || []).length > 0 && (
                                  <div className="dp-row">
                                    {p.dp.map((entry, i) => (
                                      <div
                                        key={i}
                                        className="dp-select-wrap"
                                      >
                                        <span className="dp-label-text">
                                          Unit {i + 1}
                                        </span>
                                        <select
                                          className="dp-select"
                                          value={entry?.value || "NONE"}
                                          onChange={(e) =>
                                            setDPValue(
                                              custIndex,
                                              p.name,
                                              i,
                                              e.target.value
                                            )
                                          }
                                        >
                                          <option value="NONE">No</option>
                                          <option value="YES">Yes</option>
                                          <option value="HAS">Existing</option>
                                        </select>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="admin-modal-actions">
              <button
                className="secondary-btn"
                onClick={closeEditModal}
              >
                Cancel
              </button>
              <button
                className="display-btn"
                onClick={handleSaveReport}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}